import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Pill } from "@/components/kit";
import { usd, fmtDate } from "@/lib/format";
import { ExternalLink, Copy, CheckCircle2, Sliders, Trash2, Layers } from "lucide-react";
import { toast } from "sonner";
import { CustomizeProposalModal } from "@/components/CustomizeProposalModal";
import { proposalTypeLabel } from "@/lib/roofTypes";

export default function Proposals() {
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [customizing, setCustomizing] = useState(null);
  const [applyFor, setApplyFor] = useState(null);
  const load = () => api.get("/proposals").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/scope-templates").then((r) => setTemplates(r.data)).catch(() => {}); }, []);

  const applyTpl = async (p, t) => {
    const add = (t.items || []).map((i) => ({ title: i.title, description: i.description || "", image_url: i.image_url || "" }));
    const scope_items = [...(p.scope_items || []), ...add];
    try {
      await api.put(`/proposals/${p.id}/scope`, { scope_items, upgrades: p.upgrades || [] });
      toast.success(`Added ${add.length} steps from "${t.name}"`);
      setApplyFor(null); load();
    } catch { toast.error("Could not apply template"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this proposal? This cannot be undone.")) return;
    try { await api.delete(`/proposals/${id}`); toast.success("Proposal deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const copyLink = (id) => {
    navigator.clipboard.writeText(`${window.location.origin}/proposal/${id}`);
    toast.success("Customer link copied");
  };

  return (
    <div>
      <PageHeader title="Proposals" subtitle="Interactive, hosted, e-signable — deposit collected at signature" />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((p) => (
          <div key={p.id} data-testid={`proposal-${p.id}`} className="mb-card p-5">
            <div className="flex justify-between items-start">
              <div className="font-num text-2xl font-extrabold text-navy">{usd(p.amount_cents)}</div>
              <Pill tone={p.status === "signed" ? "green" : "amber"}>{p.status?.replace("_", " ")}</Pill>
            </div>
            <div className="text-sm font-bold text-navy mt-1" data-testid={`proposal-name-${p.id}`}>{p.customer_name || "Unknown customer"}</div>
            {p.customer_address && <div className="text-xs text-slate-400 truncate">{p.customer_address}</div>}
            <div className="text-sm text-slate-500 mt-1">Roof: <b className="text-navy" data-testid={`proposal-type-${p.id}`}>{proposalTypeLabel(p)}</b> · Deposit {usd(p.deposit_cents)}</div>
            <div className="text-xs text-slate-400 mt-1">{fmtDate(p.created_at)}</div>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {p.systems?.length > 0 && <span className="text-[11px] text-navy bg-navy/10 rounded-full px-2 py-0.5">{p.systems.length} roof system{p.systems.length > 1 ? "s" : ""}</span>}
              {!p.systems?.length && p.scope_items?.length > 0 && <span className="text-[11px] text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">{p.scope_items.length} scope steps</span>}
              {p.upgrades?.length > 0 && <span className="text-[11px] text-brandred bg-brandred/10 rounded-full px-2 py-0.5">{p.upgrades.length} upgrades</span>}
            </div>
            {p.deposit_paid && <div className="mt-2 flex items-center gap-1 text-successg text-xs font-semibold"><CheckCircle2 className="w-4 h-4" /> Deposit received</div>}
            <div className="flex gap-2 mt-4">
              <a href={`/proposal/${p.id}`} target="_blank" rel="noreferrer" data-testid={`view-proposal-${p.id}`}
                className="flex-1 bg-navy text-white text-sm font-bold py-2 rounded-xl border-2 border-navy flex items-center justify-center gap-1"><ExternalLink className="w-4 h-4" /> Open</a>
              <button onClick={() => setCustomizing(p)} data-testid={`customize-${p.id}`} className="bg-white border-2 border-slate-300 text-slate-700 px-3 rounded-xl" title="Customize scope & upgrades"><Sliders className="w-4 h-4" /></button>
              {!p.systems?.length && <div className="relative">
                <button onClick={() => setApplyFor(applyFor === p.id ? null : p.id)} data-testid={`apply-template-${p.id}`} className="bg-white border-2 border-slate-300 text-slate-700 px-3 h-full rounded-xl" title="Apply a scope template"><Layers className="w-4 h-4" /></button>
                {applyFor === p.id && (
                  <div className="absolute right-0 bottom-full mb-2 z-20 w-60 bg-white border-2 border-slate-200 rounded-xl shadow-lg p-1.5" data-testid={`apply-menu-${p.id}`} onMouseLeave={() => setApplyFor(null)}>
                    <div className="text-[11px] uppercase text-slate-400 px-2 py-1">Add template steps to this proposal</div>
                    {templates.length === 0 && <div className="text-xs text-slate-400 px-2 py-2">No templates yet.</div>}
                    {templates.map((t) => (
                      <button key={t.id} data-testid={`apply-tpl-${p.id}-${t.id}`} onClick={() => applyTpl(p, t)}
                        className="w-full text-left px-2 py-1.5 rounded-lg text-sm text-navy hover:bg-slate-100 flex items-center justify-between gap-2">
                        <span className="truncate">{t.name}</span><span className="text-[11px] text-slate-400 shrink-0">{t.items?.length || 0}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>}
              <button onClick={() => copyLink(p.id)} data-testid={`copy-link-${p.id}`} className="bg-white border-2 border-slate-300 text-slate-700 px-3 rounded-xl"><Copy className="w-4 h-4" /></button>
              <button onClick={() => del(p.id)} data-testid={`delete-proposal-${p.id}`} className="bg-white border-2 border-slate-300 text-brandred px-3 rounded-xl" title="Delete proposal"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
      {items.length === 0 && <div className="mb-card p-10 text-center text-slate-400">No proposals yet. Build an estimate to generate one.</div>}
      {customizing && <CustomizeProposalModal proposal={customizing} templates={templates} onClose={() => setCustomizing(null)} onSaved={() => { setCustomizing(null); load(); }} />}
    </div>
  );
}
